import { Component } from "@theme/component";

/**
 * @typedef {object} Refs
 * @property {SVGPathElement} circle
 */

/**
 * The circle is only drawn on hover (theme.css), so a screen that can't hover
 * never measures it.
 */
const canHover = window.matchMedia("(hover: hover)");

/**
 * Samples along each distinct path, in its own user units. `getPointAtLength()`
 * is the costly part (about 9 ms for 200 of them on a fast laptop), and every
 * decoration draws the snippet's one oval, so it's sampled once a page and each
 * item only maps the samples through its own CTM.
 * @type {Map<string, DOMPoint[]>}
 */
const pathSamples = new Map();

/**
 * Computes the rendered screen-space length (in CSS pixels relative to the
 * SVG viewport) of an SVG path by sampling it in user space and transforming
 * each sample through the path's CTM. This is needed because the path is
 * drawn with `vector-effect="non-scaling-stroke"` inside a viewBox stretched
 * non-uniformly via `preserveAspectRatio="none"`, so neither `getTotalLength()`
 * nor a static `pathLength="100"` align with how the dash pattern is rendered.
 *
 * @param {SVGPathElement} path
 * @param {number} samples
 * @returns {number}
 */
function computeScreenLength(path, samples = 200) {
  const ctm = path.getCTM();
  if (!ctm) return 0;

  const shape = path.getAttribute("d") ?? "";
  let points = pathSamples.get(shape);
  if (!points) {
    const total = path.getTotalLength();
    points = [];
    for (let i = 0; i <= samples; i++) {
      points.push(path.getPointAtLength((total * i) / samples));
    }
    pathSamples.set(shape, points);
  }

  // The CTM's translation cancels out between two points, so only its linear part maps a step
  let length = 0;
  for (let i = 1; i < points.length; i++) {
    const dx = points[i].x - points[i - 1].x;
    const dy = points[i].y - points[i - 1].y;
    length += Math.hypot(ctm.a * dx + ctm.c * dy, ctm.b * dx + ctm.d * dy);
  }

  return length;
}

/**
 * @extends {Component<Refs>}
 */
class MenuItemDecoration extends Component {
  requiredRefs = ["circle"];

  /** @type {ResizeObserver | null} */
  #observer = null;

  connectedCallback() {
    super.connectedCallback();

    if (canHover.matches) this.#observe();
    else canHover.addEventListener("change", this.#onHoverChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    canHover.removeEventListener("change", this.#onHoverChange);
    this.#observer?.disconnect();
    this.#observer = null;
  }

  updatedCallback() {
    super.updatedCallback();
    if (this.#observer) this.#update();
  }

  #onHoverChange = () => {
    if (!canHover.matches) return;
    canHover.removeEventListener("change", this.#onHoverChange);
    this.#observe();
  };

  /**
   * A ResizeObserver's first call comes once the host is laid out (never while
   * it's hidden), and it calls again as the host resizes: one measurement each.
   */
  #observe() {
    const host = this.parentElement;
    if (!host || this.#observer) return;

    this.#observer = new ResizeObserver(() => this.#update());
    this.#observer.observe(host);
  }

  #update() {
    const path = this.refs.circle;
    if (!path) return;

    const length = computeScreenLength(path);
    if (length > 0) {
      this.style.setProperty("--menu-circle-dash", `${length}px`);
      if (!this.dataset.ready) {
        // Defer to next frame so the dashoffset/dasharray change above is
        // committed under `transition: none` (gated on [data-ready]) before
        // the transition rule re-enables — otherwise the offset would
        // animate from the fallback to the computed value and briefly
        // draw the circle.
        requestAnimationFrame(() => {
          this.dataset.ready = "";
        });
      }
    }
  }
}

if (!customElements.get("menu-item-decoration")) {
  customElements.define("menu-item-decoration", MenuItemDecoration);
}
